import { Fragment, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { css, cx } from "styled-system/css";
import { ak } from "./admin-styles";
import { Banner, PageHead, Section } from "~/components/editorial";
import { LedgerRow } from "./admin-kit";

type DeveloperRole = "DEVELOPER" | "PRODUCT" | "QA" | "OTHER";

const ROLE_LABELS: Record<DeveloperRole, string> = {
  DEVELOPER: "Developer",
  PRODUCT: "Product",
  QA: "QA",
  OTHER: "Other",
};

interface Developer {
  id: string;
  displayName: string;
  email: string | null;
  githubUsername: string | null;
  githubId: string | null;
  jiraAccountId: string | null;
  jiraDisplayName: string | null;
  avatarUrl: string | null;
  role: DeveloperRole;
  _count: {
    pullRequests: number;
    reviews: number;
    commits: number;
    jiraIssues: number;
  };
}

interface IdentitiesResponse {
  data: {
    all: Developer[];
    linked: Developer[];
    githubOnly: Developer[];
    jiraOnly: Developer[];
    unlinked: Developer[];
    counts: {
      total: number;
      linked: number;
      githubOnly: number;
      jiraOnly: number;
      unlinked: number;
    };
  };
}

interface AutoMergeResponse {
  data: {
    merged: Array<{ githubDev: string; jiraDev: string; confidence: number }>;
    count: number;
  };
}

interface UnlinkedEmail {
  email: string;
  name: string;
  commitCount: number;
}

type TabType = "all" | "linked" | "githubOnly" | "jiraOnly" | "unlinked";

export function DeveloperIdentitiesPage() {
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [linking, setLinking] = useState<string | null>(null);
  const [autoMerging, setAutoMerging] = useState(false);
  const [lastMergeResult, setLastMergeResult] = useState<{
    count: number;
    merged: Array<{ githubDev: string; jiraDev: string }>;
  } | null>(null);
  const [search, setSearch] = useState("");
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [linkingEmail, setLinkingEmail] = useState<string | null>(null);
  const [mergingDev, setMergingDev] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const identitiesQuery = useQuery({
    queryKey: ["developer-identities"],
    queryFn: async () => {
      const response = await fetch("/api/developers/identities");
      const result: IdentitiesResponse = await response.json();
      return result.data ?? null;
    },
    staleTime: 0,
    retry: false,
  });
  const data = identitiesQuery.data ?? null;
  const loading = identitiesQuery.isLoading;
  const error = identitiesQuery.isError
    ? "Failed to load developer identities"
    : null;
  const fetchIdentities = async () => {
    await identitiesQuery.refetch();
  };
  const setData = (next: IdentitiesResponse["data"]) =>
    queryClient.setQueryData(["developer-identities"], next);

  const unlinkedQuery = useQuery({
    queryKey: ["developer-unlinked-emails"],
    queryFn: async () => {
      const response = await fetch("/api/developers/unlinked-emails");
      const result = await response.json();
      return (result.data || []) as UnlinkedEmail[];
    },
    staleTime: 0,
    retry: false,
  });
  const unlinkedEmails = unlinkedQuery.data ?? [];
  const fetchUnlinkedEmails = async () => {
    await unlinkedQuery.refetch();
  };

  const handleAutoMerge = async () => {
    if (
      !confirm(
        "This will automatically merge high-confidence JIRA profiles into their matching GitHub profiles.\n\nProfiles with 70%+ confidence will be merged. Continue?",
      )
    ) {
      return;
    }

    try {
      setAutoMerging(true);
      const response = await fetch("/api/developers/auto-merge", {
        method: "POST",
      });
      const result: AutoMergeResponse = await response.json();
      setLastMergeResult({
        count: result.data.count,
        merged: result.data.merged,
      });
      await fetchIdentities();
    } catch (err) {
      alert("Auto-merge failed");
      console.error(err);
    } finally {
      setAutoMerging(false);
    }
  };

  const handleLinkJira = async (githubDevId: string, jiraDevId: string) => {
    if (!jiraDevId) return;

    const jiraDev = data?.jiraOnly.find((d) => d.id === jiraDevId);
    const githubDev = data?.all.find((d) => d.id === githubDevId);

    if (
      !confirm(
        `Link "${jiraDev?.jiraDisplayName || jiraDev?.displayName}" to "${githubDev?.displayName}"?\n\nThis will merge the JIRA profile into the GitHub profile.`,
      )
    ) {
      return;
    }

    try {
      setLinking(githubDevId);
      const response = await fetch(`/api/developers/${githubDevId}/link-jira`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jiraDevId }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || "Link failed");
      }

      await fetchIdentities();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to link profiles");
    } finally {
      setLinking(null);
    }
  };

  const handleUnlinkJira = async (devId: string) => {
    const dev = data?.all.find((d) => d.id === devId);
    if (
      !confirm(
        `Unlink JIRA identity "${dev?.jiraDisplayName}" from "${dev?.displayName}"?\n\nThis will create a separate JIRA-only profile.`,
      )
    ) {
      return;
    }

    try {
      setLinking(devId);
      const response = await fetch(`/api/developers/${devId}/unlink-jira`, {
        method: "POST",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || "Unlink failed");
      }

      await fetchIdentities();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to unlink profiles");
    } finally {
      setLinking(null);
    }
  };

  const handleAddEmailAlias = async (developerId: string, email: string) => {
    if (!developerId) return;

    const dev = data?.all.find((d) => d.id === developerId);

    try {
      setLinkingEmail(email);
      const response = await fetch(`/api/developers/${developerId}/aliases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const respData = await response.json();
        throw new Error(respData.error?.message || "Failed to add email alias");
      }

      const result = await response.json();
      alert(
        `Added email alias "${email}" to ${dev?.displayName || "developer"}.\n\nLinked ${result.data.linkedCommits} commits.`,
      );

      // Refresh data
      await fetchIdentities();
      await fetchUnlinkedEmails();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to add email alias");
    } finally {
      setLinkingEmail(null);
    }
  };

  const handleMergeDeveloper = async (targetId: string, sourceId: string) => {
    const sourceDev = data?.unlinked.find((d) => d.id === sourceId);
    const targetDev = data?.all.find((d) => d.id === targetId);

    if (
      !confirm(
        `Merge "${sourceDev?.displayName}" into "${targetDev?.displayName}"?\n\nThis will:\n- Transfer all commits to the target profile\n- Add email aliases for commit attribution\n- Delete the orphaned profile\n\nThis cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setMergingDev(sourceId);
      const response = await fetch(
        `/api/developers/${targetId}/merge/${sourceId}`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        const respData = await response.json();
        throw new Error(respData.error?.message || "Merge failed");
      }

      const result = await response.json();
      alert(
        `Successfully merged developer profiles.\n\nTransferred ${result.data.mergedCommits} commits.`,
      );

      await fetchIdentities();
      await fetchUnlinkedEmails();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to merge developers");
    } finally {
      setMergingDev(null);
    }
  };

  const handleRoleChange = async (devId: string, newRole: DeveloperRole) => {
    try {
      setUpdatingRole(devId);
      const response = await fetch(`/api/developers/${devId}/role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        const respData = await response.json();
        throw new Error(respData.error?.message || "Role update failed");
      }

      // Update local state to avoid full refetch
      if (data) {
        const updateDevs = (devs: Developer[]) =>
          devs.map((d) => (d.id === devId ? { ...d, role: newRole } : d));
        setData({
          ...data,
          all: updateDevs(data.all),
          linked: updateDevs(data.linked),
          githubOnly: updateDevs(data.githubOnly),
          jiraOnly: updateDevs(data.jiraOnly),
          unlinked: updateDevs(data.unlinked),
        });
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setUpdatingRole(null);
    }
  };

  const getDisplayedDevelopers = (): Developer[] => {
    if (!data) return [];

    let devs: Developer[];
    switch (activeTab) {
      case "linked":
        devs = data.linked;
        break;
      case "githubOnly":
        devs = data.githubOnly;
        break;
      case "jiraOnly":
        devs = data.jiraOnly;
        break;
      case "unlinked":
        devs = data.unlinked;
        break;
      default:
        devs = data.all;
    }

    if (search) {
      const s = search.toLowerCase();
      devs = devs.filter(
        (d) =>
          d.displayName.toLowerCase().includes(s) ||
          d.githubUsername?.toLowerCase().includes(s) ||
          d.jiraDisplayName?.toLowerCase().includes(s) ||
          d.email?.toLowerCase().includes(s),
      );
    }

    return devs;
  };

  const displayedDevelopers = getDisplayedDevelopers();
  const counts = data?.counts;

  const TABS: Array<{ id: TabType; label: string; count: number }> = [
    { id: "all", label: "All", count: counts?.total ?? 0 },
    { id: "linked", label: "Linked", count: counts?.linked ?? 0 },
    { id: "githubOnly", label: "GitHub only", count: counts?.githubOnly ?? 0 },
    { id: "jiraOnly", label: "Jira only", count: counts?.jiraOnly ?? 0 },
    { id: "unlinked", label: "Unlinked", count: counts?.unlinked ?? 0 },
  ];

  return (
    <div className={ak.page}>
      <PageHead
        back={{ to: "/settings", label: "Settings" }}
        kicker="Admin · Identities"
        title={counts ? identityHeadline(counts) : "Contributor identities"}
        dek={
          counts ? (
            <IdentityDek counts={counts} emails={unlinkedEmails.length} />
          ) : (
            "Link GitHub and Jira profiles, merge duplicates and attach commit emails."
          )
        }
        actions={
          <button
            className={ak.btn}
            onClick={handleAutoMerge}
            disabled={autoMerging}
          >
            {autoMerging ? "Merging…" : "Auto-merge Jira profiles"}
          </button>
        }
      />

      <LedgerRow
        items={[
          {
            label: "Linked",
            value: counts?.linked ?? "–",
            note: "GitHub and Jira",
          },
          {
            label: "GitHub only",
            value: counts?.githubOnly ?? "–",
            note: "no Jira profile",
          },
          {
            label: "Jira only",
            value: counts?.jiraOnly ?? "–",
            note: "no GitHub profile",
          },
          {
            label: "Unlinked",
            value: counts?.unlinked ?? "–",
            note: "commit author only",
          },
          {
            label: "Loose emails",
            value: unlinkedEmails.length,
            note: "commits with no owner",
          },
        ]}
      />

      {lastMergeResult && lastMergeResult.count > 0 && (
        <Banner>
          Merged <span className={ak.mono}>{lastMergeResult.count}</span>{" "}
          {lastMergeResult.count === 1 ? "profile" : "profiles"}.
        </Banner>
      )}

      <Section kicker="I. Profiles" title="Every contributor profile">
        <div className={styles.filterRow}>
          <div className={ak.tabs} role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeTab === t.id}
                className={ak.tab}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
                <span className="num">{t.count}</span>
              </button>
            ))}
          </div>
          <input
            type="search"
            placeholder="Search name, handle or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cx(ak.input, styles.search)}
            aria-label="Search developers"
          />
        </div>

        {loading ? (
          <div className={ak.loading}>Loading developer profiles…</div>
        ) : error ? (
          <Banner tone="caution">
            {error}{" "}
            <button className={ak.btnText} onClick={fetchIdentities}>
              Retry
            </button>
          </Banner>
        ) : displayedDevelopers.length === 0 ? (
          <div className={ak.empty}>
            <p>No developers match.</p>
          </div>
        ) : (
          <div className={ak.tableWrap}>
            <table className={ak.table}>
              <thead>
                <tr>
                  <th>Developer</th>
                  <th>Role</th>
                  <th>GitHub</th>
                  <th data-num>PRs</th>
                  <th>Jira</th>
                  <th data-num>Issues</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedDevelopers.map((dev) => (
                  <tr key={dev.id}>
                    <td>
                      <div className={styles.devCell}>
                        {dev.avatarUrl ? (
                          <img
                            src={dev.avatarUrl}
                            alt=""
                            className={cx(ak.avatar, styles.avatar)}
                          />
                        ) : (
                          <span className={styles.initial} aria-hidden>
                            {dev.displayName.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <div className={styles.devText}>
                          <div className={styles.devName}>
                            {dev.displayName}
                          </div>
                          {dev.email && (
                            <div className={cx(ak.mono, styles.devEmail)}>
                              {dev.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td>
                      <select
                        className={cx(ak.select, ak.selectSm)}
                        value={dev.role}
                        aria-label={`Role for ${dev.displayName}`}
                        onChange={(e) =>
                          handleRoleChange(
                            dev.id,
                            e.target.value as DeveloperRole,
                          )
                        }
                        disabled={updatingRole === dev.id}
                      >
                        {(Object.keys(ROLE_LABELS) as DeveloperRole[]).map(
                          (role) => (
                            <option key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </option>
                          ),
                        )}
                      </select>
                    </td>

                    <td className={ak.mono}>
                      {dev.githubUsername ? (
                        `@${dev.githubUsername}`
                      ) : (
                        <span className={ak.muted}>–</span>
                      )}
                    </td>
                    <td data-num>
                      {dev.githubUsername ? dev._count.pullRequests : ""}
                    </td>

                    <td>
                      {dev.jiraAccountId ? (
                        dev.jiraDisplayName || "Linked"
                      ) : dev.githubUsername &&
                        data?.jiraOnly &&
                        data.jiraOnly.length > 0 ? (
                        <select
                          className={cx(ak.select, ak.selectSm)}
                          value=""
                          aria-label={`Link a Jira profile to ${dev.displayName}`}
                          onChange={(e) =>
                            handleLinkJira(dev.id, e.target.value)
                          }
                          disabled={linking === dev.id}
                        >
                          <option value="">Link Jira profile…</option>
                          {data.jiraOnly.map((jiraDev) => (
                            <option key={jiraDev.id} value={jiraDev.id}>
                              {jiraDev.jiraDisplayName || jiraDev.displayName} (
                              {jiraDev._count.jiraIssues} issues)
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={ak.muted}>–</span>
                      )}
                    </td>
                    <td data-num>
                      {dev.jiraAccountId ? dev._count.jiraIssues : ""}
                    </td>

                    <td>
                      {dev.githubUsername && dev.jiraAccountId && (
                        <button
                          className={cx(ak.btnSecondary, ak.btnSm)}
                          onClick={() => handleUnlinkJira(dev.id)}
                          disabled={linking === dev.id}
                          title="Unlink Jira"
                        >
                          Unlink
                        </button>
                      )}
                      {/* Merge option for unlinked developers (no GitHub, no JIRA) */}
                      {!dev.githubUsername && !dev.jiraAccountId && (
                        <select
                          className={cx(ak.select, ak.selectSm)}
                          value=""
                          aria-label={`Merge ${dev.displayName} into another profile`}
                          onChange={(e) =>
                            handleMergeDeveloper(e.target.value, dev.id)
                          }
                          disabled={mergingDev === dev.id}
                        >
                          <option value="">Merge into…</option>
                          {data?.all
                            .filter((d) => d.id !== dev.id && d.githubUsername)
                            .map((targetDev) => (
                              <option key={targetDev.id} value={targetDev.id}>
                                {targetDev.displayName} (@
                                {targetDev.githubUsername})
                              </option>
                            ))}
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className={ak.note}>
          Linked means one profile carries both a GitHub login and a Jira
          account. Auto-merge folds a Jira-only profile into a GitHub profile
          when the match is at least 70% confident. Unlinked profiles come from
          commit authors with neither.
        </p>
      </Section>

      {/* Unlinked Commit Emails Section */}
      {unlinkedEmails.length > 0 && (
        <Section
          kicker="II. Commit emails"
          title="Commit emails that belong to no one"
          dek="These addresses appear on commits but match no profile. Link each to a developer so the commits count."
        >
          <div className={ak.tableWrap}>
            <table className={ak.table}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Commit name</th>
                  <th data-num>Commits</th>
                  <th>Link to</th>
                </tr>
              </thead>
              <tbody>
                {unlinkedEmails.map((item) => (
                  <tr key={item.email}>
                    <td className={ak.mono}>{item.email}</td>
                    <td>{item.name}</td>
                    <td data-num>{item.commitCount}</td>
                    <td>
                      <select
                        className={cx(ak.select, ak.selectSm)}
                        value=""
                        aria-label={`Link ${item.email} to a developer`}
                        onChange={(e) =>
                          handleAddEmailAlias(e.target.value, item.email)
                        }
                        disabled={linkingEmail === item.email}
                      >
                        <option value="">Link to developer…</option>
                        {data?.all
                          .filter((d) => d.githubUsername)
                          .map((dev) => (
                            <option key={dev.id} value={dev.id}>
                              {dev.displayName} (@{dev.githubUsername})
                            </option>
                          ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={ak.note}>
            Linking adds the address as an alias and reattributes its commits.
          </p>
        </Section>
      )}
    </div>
  );
}

type Counts = IdentitiesResponse["data"]["counts"];

function identityHeadline(c: Counts): string {
  if (c.total === 0) return "No contributor profiles yet";
  if (c.linked === c.total) {
    return `All ${c.total} contributors are linked across GitHub and Jira`;
  }
  return `${c.linked} of ${c.total} contributors are linked across GitHub and Jira`;
}

function IdentityDek({ counts, emails }: { counts: Counts; emails: number }) {
  const parts: React.ReactNode[] = [];
  if (counts.githubOnly > 0) {
    parts.push(
      <Fragment key="gh">
        <span className={ak.mono}>{counts.githubOnly}</span>{" "}
        {counts.githubOnly === 1 ? "has" : "have"} GitHub only
      </Fragment>,
    );
  }
  if (counts.jiraOnly > 0) {
    parts.push(
      <Fragment key="jira">
        <span className={ak.mono}>{counts.jiraOnly}</span>{" "}
        {counts.jiraOnly === 1 ? "has" : "have"} Jira only
      </Fragment>,
    );
  }
  if (counts.unlinked > 0) {
    parts.push(
      <Fragment key="none">
        <span className={ak.mono}>{counts.unlinked}</span>{" "}
        {counts.unlinked === 1 ? "has" : "have"} neither
      </Fragment>,
    );
  }
  return (
    <>
      {parts.length > 0 ? (
        <>
          {parts.map((p, i) => (
            <Fragment key={i}>
              {i > 0 && (i === parts.length - 1 ? " and " : ", ")}
              {p}
            </Fragment>
          ))}
          .
        </>
      ) : (
        "Nothing left to link."
      )}{" "}
      {emails > 0 && (
        <>
          <span className={ak.mono}>{emails}</span> commit{" "}
          {emails === 1 ? "email matches" : "emails match"} no one.
        </>
      )}
    </>
  );
}

const styles = {
  filterRow: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "12px 24px",
    mb: "12px",
  }),
  search: css({ maxWidth: "260px", fontSize: "13px!" }),
  devCell: css({ display: "flex", alignItems: "center", gap: "10px" }),
  avatar: css({ width: "24px!", height: "24px!" }),
  initial: css({
    width: "24px",
    height: "24px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderColor: "ed.rule",
    fontFamily: "serif",
    fontSize: "13px",
    fontWeight: 700,
    color: "ed.ink2",
  }),
  devText: css({ minWidth: 0 }),
  devName: css({ fontWeight: 600 }),
  devEmail: css({ fontSize: "11px", color: "ed.ink3" }),
};
